import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAsns } from "@/lib/receiving.functions";
import { ScanInput } from "@/components/ui/scan-input";
import { RfShell, RfRow, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { useScope } from "@/hooks/use-scope";
import { StatusPill } from "@/components/ui/status-pill";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/rf/receiving")({
  component: RfReceiving,
});

function RfReceiving() {
  const { tenantId, facilityId, customerId } = useScope();
  const nav = useNavigate();
  const listFn = useServerFn(listAsns);

  const { data: asns = [], isLoading } = useQuery({
    queryKey: ["rf-asns", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null, sort: "desc" } }),
  });

  const open = useMemo(
    () => (asns as any[]).filter((a) => a.status !== "closed" && a.status !== "cancelled"),
    [asns],
  );

  function handleScan(code: string) {
    const match = (asns as any[]).find(
      (a) => (a.reference ?? "").toLowerCase() === code.toLowerCase() || a.id === code,
    );
    if (!match) {
      toast.error(`No open ASN matches "${code}"`);
      return;
    }
    nav({ to: "/app/rf/receiving/$asnId", params: { asnId: match.id } });
  }

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="Receiving" back>
        <RfScopeMissing />
      </RfShell>
    );
  }

  return (
    <RfShell title="Receiving" back>
      <ScanInput onScan={handleScan} placeholder="Scan ASN reference…" size="lg" />
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${open.length} open`}
      </div>
      <div className="space-y-2">
        {open.length === 0 && !isLoading ? (
          <RfEmpty>No open ASNs at this facility.</RfEmpty>
        ) : (
          open.map((a) => (
            <RfRow
              key={a.id}
              to={`/app/rf/receiving/${a.id}` as any}
              primary={<span className="font-mono">{a.reference ?? a.id.slice(0, 8)}</span>}
              secondary={a.carrier ?? "—"}
              trailing={<StatusPill tone="info">{a.status}</StatusPill>}
            />
          ))
        )}
      </div>
    </RfShell>
  );
}